import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, type Article } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article: Article | undefined = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="pt-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-28 text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-error-warning-line text-4xl"></i>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
          <p className="text-foreground-500 mb-8">您访问的文章不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const categoryData = categories.find((c) => c.slug === article.category);

  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      '@type': 'Organization',
      name: '17c.cc',
      url: 'https://17c.cc',
    },
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '17c.cc 首页', item: 'https://17c.cc/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryData?.name || article.category,
        item: `https://17c.cc/category/${article.category}`,
      },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`17c.cc,${article.tags.join(',')},17c.cc使用指南`}
        canonicalUrl={`https://17c.cc/article/${article.slug}`}
        ogType="article"
        structuredData={{ ...articleStructuredData, ...breadcrumbStructuredData }}
      />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryData?.name || article.category, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <header className="mb-10">
              <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-foreground-500">
                <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium">
                  {categoryData?.name || article.category}
                </span>
                <span>{article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>阅读约 {article.readTime}</span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-6">
                {article.title}
              </h1>

              <p className="text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <footer className="mt-14 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回 {categoryData?.name || article.category}
                </Link>

                <div className="flex gap-3">
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${article.category}`}
                      className="text-xs text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            </footer>
          </div>
        </article>

        <section className="py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-8">
              相关文章
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles
                .filter((a) => a.slug !== article.slug && a.category === article.category)
                .slice(0, 3)
                .map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-500 line-clamp-2">
                      {related.excerpt}
                    </p>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}